import { AuthService } from '@/services/auth.service'
import networkNav from '@/navigation/vertical/index'
import academyNav from '@/navigation/vertical/academy'
import agencyNav from '@/navigation/vertical/agency'
import aiNav from '@/navigation/vertical/ai'
import clubNav from '@/navigation/vertical/club'
import communityNav from '@/navigation/vertical/community'
import jobsNav from '@/navigation/vertical/jobs'
import shopNav from '@/navigation/vertical/shop'
import tokenNav from '@/navigation/vertical/token'
import tvNav from '@/navigation/vertical/tv'
import venturesNav from '@/navigation/vertical/ventures'

const PLATFORMS = [
	{ key: 'network', label: 'Network', items: networkNav },
	{ key: 'community', label: 'Community', items: communityNav },
	{ key: 'agency', label: 'Agency', items: agencyNav },
	{ key: 'tv', label: 'TV', items: tvNav },
	{ key: 'jobs', label: 'Jobs', items: jobsNav },
	{ key: 'academy', label: 'Academy', items: academyNav },
	{ key: 'ai', label: 'AI', items: aiNav },
	{ key: 'shop', label: 'Deals', items: shopNav },
	{ key: 'club', label: 'Club', items: clubNav },
	{ key: 'token', label: 'Token', items: tokenNav },
	{ key: 'ventures', label: 'Ventures', items: venturesNav },
]

function checkPermission(item) {
	const service = AuthService._instance
	if (!service) return false

	let user
	try {
		user = service.getUser()
	} catch (e) {
		return false
	}

	if (!user || !user.acl_permissions) return false

	if (user.acl_permissions.roles.includes('MGN,admin_master')) return true

	if (item.roles) {
		const roles = Array.isArray(item.roles) ? [...item.roles] : [item.roles]
		for (const role of roles) {
			const system = role.split(',')[0]
			if (!roles.includes(`${system},admin`)) roles.push(`${system},admin`)
		}
		const hasRole = roles.some(r => user.acl_permissions.roles.includes(r))
		if (!hasRole) return false
	}

	if (item.module) {
		const hasModule = user.acl_permissions.modules.includes(item.module)
		if (!hasModule) return false
	}

	return true
}

function flattenItems(items, platform, translateFn, parentLabel = '') {
	const result = []
	for (const item of items) {
		// skip external links (href) - no internal route
		if (item.href) continue

		const rawTitle = item.title || ''
		// resolve i18n key or use as-is
		const resolvedTitle = rawTitle.startsWith('menu.') || rawTitle.includes('.')
			? translateFn(rawTitle) || rawTitle
			: rawTitle

		const breadcrumb = parentLabel ? `${parentLabel} › ${resolvedTitle}` : resolvedTitle

		if (item.to) {
			result.push({
				id: `nav:${platform.key}:${item.to}`,
				title: resolvedTitle,
				breadcrumb,
				platform: platform.label,
				platformKey: platform.key,
				routeName: item.to,
				icon: item.icon || null,
				// keep for permission check at search time
				_module: item.module || null,
				_roles: item.roles || null,
			})
		}

		if (item.children) {
			result.push(...flattenItems(item.children, platform, translateFn, breadcrumb))
		}
	}
	return result
}

/**
 * Returns nav items that match the query and pass permission checks.
 * @param {string} query
 * @param {Function} translateFn - $t from the component
 * @returns {Array}
 */
export function searchNavItems(query, translateFn) {
	if (!query || query.length < 2) return []

	const q = query.toLowerCase().trim()
	const results = []

	for (const platform of PLATFORMS) {
		const flat = flattenItems(platform.items, platform, translateFn)
		for (const item of flat) {
			const matchTitle = item.title.toLowerCase().includes(q)
			const matchBreadcrumb = item.breadcrumb.toLowerCase().includes(q)
			const matchPlatform = item.platform.toLowerCase().includes(q)

			if (!matchTitle && !matchBreadcrumb && !matchPlatform) continue

			// permission check using item's own module/roles
			const permItem = { module: item._module, roles: item._roles }
			if (!checkPermission(permItem)) continue

			results.push(item)
		}
	}

	return results
}
